import { ReporteChequeo, ReporteRow } from "../models/ReporteChequeo.ts";
import * as moment from "moment-timezone";
import { buildVisitRowsForPartnerBranch } from "./visitPairingReporteChequeo.ts";
import {
  buildReporteChequeoRows,
  BranchReportMeta,
} from "./operationalDayReporteChequeo.ts";
import { effectiveNightMetaForPartner } from "./nightScheduleEligibility.ts";

export type { BranchReportMeta };

function isBranchOperationalMeta(
  x: Record<number, number> | Record<number, BranchReportMeta> | undefined
): x is Record<number, BranchReportMeta> {
  if (!x || Object.keys(x).length === 0) return false;
  const first = Object.values(x)[0] as unknown;
  return (
    first !== null &&
    typeof first === "object" &&
    "nightScheduleEnabled" in (first as BranchReportMeta)
  );
}

/** Orden cronológico por hora de entrada; mismo colaborador agrupado antes que mezclar personas. */
export function sortReporteRowsChronologically(rows: ReporteRow[]): ReporteRow[] {
  return [...rows].sort((a, b) => {
    const pa = a.checkIn?.partner_id ?? 0;
    const pb = b.checkIn?.partner_id ?? 0;
    if (pa !== pb) {
      return pa - pb;
    }
    const dateA = a.checkIn?.assistanceCheck_checkDate ?? "";
    const dateB = b.checkIn?.assistanceCheck_checkDate ?? "";
    const dc = dateA.localeCompare(dateB);
    if (dc !== 0) {
      return dc;
    }
    return (a.checkIn?.assistanceCheck_checkTime ?? "").localeCompare(
      b.checkIn?.assistanceCheck_checkTime ?? ""
    );
  });
}

function formatDataForReportLegacy(
  records: ReporteChequeo[],
  start: string,
  end: string,
  branchCutoffMap?: Record<number, number> | Record<number, BranchReportMeta>
): ReporteRow[] {
  const days = [
    ...new Set(records.map((record) => record.assistanceCheck_checkDate)),
  ];

  const data: ReporteRow[] = [];

  const startDateUTC = moment.utc(start).startOf("day");
  const endDateUTC = moment.utc(end).endOf("day");

  for (const day of days) {
    const currentDayUTC = moment.utc(day).startOf("day");
    const nextDayUTC = moment.utc(day).add(1, "day").startOf("day");

    if (
      currentDayUTC.isSameOrAfter(startDateUTC, "day") &&
      currentDayUTC.isSameOrBefore(endDateUTC, "day")
    ) {
      const checksCurrent = records.filter((record) => {
        const recordDate = moment
          .utc(record.assistanceCheck_checkDate)
          .startOf("day");
        return recordDate.isSame(currentDayUTC, "day");
      });
      const checksNext = records.filter((record) => {
        const recordDate = moment
          .utc(record.assistanceCheck_checkDate)
          .startOf("day");
        return recordDate.isSame(nextDayUTC, "day");
      });
      const partners = [
        ...new Set(checksCurrent.map((record) => record.partner_id)),
      ];

      for (const partner of partners) {
        const branchesCurrent = checksCurrent
          .filter((record) => record.partner_id === partner)
          .map((record) => record.branch_id);

        const branchesNext = checksNext
          .filter((record) => record.partner_id === partner)
          .map((record) => record.branch_id);

        const branches = [...new Set([...branchesCurrent, ...branchesNext])];

        for (const branch of branches) {
          const branchId = Number(branch);
          const partnerShift = checksCurrent.find(
            (r) => r.partner_id === partner
          )?.candidate_shift_name;
          const operationalMeta = isBranchOperationalMeta(branchCutoffMap)
            ? branchCutoffMap[branchId]
            : undefined;
          const effectiveMeta = effectiveNightMetaForPartner(
            operationalMeta,
            partnerShift
          );
          const cutoffMinutes = isBranchOperationalMeta(branchCutoffMap)
            ? effectiveMeta.cutoffMinutes
            : Number(
                (branchCutoffMap as Record<number, number> | undefined)?.[
                  branchId
                ]
              ) || 3 * 60;
          const nightScheduleEnabled = effectiveMeta.nightScheduleEnabled;
          const checkCurrent = checksCurrent
            .filter(
              (record) =>
                record.partner_id === partner && record.branch_id === branch
            )
            .sort((a, b) =>
              a.assistanceCheck_checkTime.localeCompare(
                b.assistanceCheck_checkTime
              )
            );

          const checkNext = checksNext
            .filter(
              (record) =>
                record.partner_id === partner && record.branch_id === branch
            )
            .sort((a, b) =>
              a.assistanceCheck_checkTime.localeCompare(
                b.assistanceCheck_checkTime
              )
            );

          const visitRows = buildVisitRowsForPartnerBranch(
            checkCurrent,
            checkNext,
            branch,
            partner,
            checksCurrent,
            checksNext,
            cutoffMinutes,
            nightScheduleEnabled
          );
          for (const row of visitRows) {
            data.push(row);
          }
        }
      }
    }
  }

  return sortReporteRowsChronologically(data);
}

/**
 * Formatea los registros de chequeo (misma lógica que el reporte de reloj).
 * Cuarto argumento:
 * - `Record<number, BranchReportMeta>`: si alguna sucursal tiene `nightScheduleEnabled`, agrupa por día operativo.
 * - `Record<number, number>` (mapa solo de cortes en minutos): comportamiento legacy por día calendario.
 */
export const formatDataForReport = (
  records: ReporteChequeo[],
  start: string,
  end: string,
  branchArg?: Record<number, number> | Record<number, BranchReportMeta>
): ReporteRow[] => {
  if (isBranchOperationalMeta(branchArg)) {
    return buildReporteChequeoRows(records, start, end, branchArg);
  }

  return formatDataForReportLegacy(
    records,
    start,
    end,
    branchArg as Record<number, number> | undefined
  );
};
